import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    Clock,
    AlertCircle,
    FileText,
    Download,
    ChevronDown,
    Briefcase,
    MapPin,
    Loader,
} from "lucide-react";
import {
    adminMissionDecisionRequest,
    getAllMissionRequestsRequest,
} from "../../../../redux/actions/missionRequestActions";
import "../TraitementStyles/TraitementOrdreMission.css";
import { API_BASE_URL } from "../../../../../api";

const TraitementMissions = () => {
    const dispatch = useDispatch();

    // Redux state - Ã  adapter selon ton slice exact
    const { allRequests, loading } = useSelector((state) => state.missionRequests || {
        allRequests: [],
        loading: false,
    });
    const demandes = allRequests || [];

    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const [filters, setFilters] = useState({
        workflow: "",
        lieu: "",
        dateDebut: "",
        dateFin: "",
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAllMissionRequestsRequest());
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const getMissionLocation = (demande) =>
        demande?.ordreMissionLieu || demande?.ordreMissionVille || demande?.ordreMissionAdresse || demande?.ordreMissionPays || "";

    const getDownloadUrl = (path) =>
        `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path || "")}`;

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente du manager", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "En attente de l'admin RH",    bg: "#fff3cd", color: "#856404" },
            APPROVED:        { label: "Approuvé",       bg: "#d4edda", color: "#155724" },
            REJECTED:        { label: "Rejeté",         bg: "#f8d7da", color: "#721c24" },
            CANCELLED:       { label: "Annulé",         bg: "#e2e8f0", color: "#4b5563" },
        };
        const c = config[statut] || config.PENDING_ADMIN;
        return (
            <span className="tomBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const handleApprove = (demandeId) => {
        dispatch(adminMissionDecisionRequest({
            id: demandeId,
            decision: true,
            comment: ""
        }));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedDemande) return;

        dispatch(adminMissionDecisionRequest({
            id: selectedDemande.id,
            decision: false,
            comment: motifRejet
        }));

        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    const mapStatutToTab = (statut) => {
        if (statut === "PENDING_ADMIN") return "enCours";
        if (statut === "APPROVED") return "approuve";
        if (statut === "REJECTED") return "rejete";
        return "";
    };

    const getFilteredDemandes = () => {
        if (!demandes || demandes.length === 0) return [];

        let filtered = demandes.filter((d) => mapStatutToTab(d.status) === activeTab);

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    (d.employeeName?.toLowerCase().includes(s)) ||
                    (d.employeeMatricule?.toLowerCase().includes(s)) ||
                    (d.ordreMissionLibelle?.toLowerCase().includes(s)) ||
                    (d.ordreMissionCode?.toLowerCase().includes(s))
            );
        }

        if (filters.workflow) {
            filtered = filtered.filter((d) => d.workflowLevel === filters.workflow);
        }

        if (filters.lieu) {
            filtered = filtered.filter((d) =>
                d.ordreMissionLieu?.toLowerCase().includes(filters.lieu.toLowerCase())
            );
        }

        if (filters.dateDebut) {
            filtered = filtered.filter((d) => {
                const date = new Date(d.createdAt).toISOString().split('T')[0];
                return date >= filters.dateDebut;
            });
        }

        if (filters.dateFin) {
            filtered = filtered.filter((d) => {
                const date = new Date(d.createdAt).toISOString().split('T')[0];
                return date <= filters.dateFin;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        if (!demandes) return [
            { id: "enCours",  label: "En cours",  count: 0 },
            { id: "approuve", label: "Approuvées", count: 0 },
            { id: "rejete",   label: "Rejetées",  count: 0 },
        ];

        return [
            {
                id: "enCours",
                label: "En cours",
                count: demandes.filter(d => d.status === "PENDING_ADMIN").length
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: demandes.filter(d => d.status === "APPROVED").length
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: demandes.filter(d => d.status === "REJECTED").length
            },
        ];
    }, [demandes]);

    const workflowsDisponibles = useMemo(() => {
        if (!demandes) return [];
        return [...new Set(demandes.map(d => d.workflowLevel).filter(Boolean))];
    }, [demandes]);

    const filtered = getFilteredDemandes();

    if (loading && (!demandes || demandes.length === 0)) {
        return (
            <div className="tomPage">
                <div className="tomWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tomPage">
            <div className="tomWrap">
                {/* Header */}
                <div className="tomHeader">
                    <div>
                        <h1>Traitement des demandes de mission</h1>
                        <p>Gérez et validez les demandes de participation aux ordres de mission</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tomCard">
                    <div className="tomControls">
                        <div className="tomSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, code mission..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button type="button" className="tomFilterBtn" onClick={() => setShowFilters(v => !v)}>
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tomChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tomFilters">
                            <div className="tomField">
                                <label>Workflow</label>
                                <select value={filters.workflow} onChange={e => setFilters(p => ({ ...p, workflow: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {workflowsDisponibles.map(w => (
                                        <option key={w} value={w}>
                                            {w === "manager" ? "Manager seul" :
                                                w === "admin"   ? "RH seul" :
                                                    w === "both"    ? "Manager + RH" : w}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="tomField">
                                <label>Lieu</label>
                                <input
                                    type="text"
                                    value={filters.lieu}
                                    onChange={e => setFilters(p => ({ ...p, lieu: e.target.value }))}
                                    placeholder="Casablanca, Rabat..."
                                />
                            </div>

                            <div className="tomField">
                                <label>Date début</label>
                                <input
                                    type="date"
                                    value={filters.dateDebut}
                                    onChange={e => setFilters(p => ({ ...p, dateDebut: e.target.value }))}
                                />
                            </div>

                            <div className="tomField">
                                <label>Date fin</label>
                                <input
                                    type="date"
                                    value={filters.dateFin}
                                    onChange={e => setFilters(p => ({ ...p, dateFin: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="tomCard tomCard--noPad">
                    <div className="tomTabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tomTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tomCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tomTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tomEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tomTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Mission</th>
                                    <th>Période</th>
                                    <th>Lieu</th>
                                    <th>Justif.</th>
                                    <th>Date de la demande</th>
                                    {/*<th>Statut</th>*/}
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        className={demande.urgent ? "tomRowUrgent" : ""}
                                        onClick={() => showDetails(demande)}
                                        title={demande.urgent ? "Demande urgente" : ""}
                                    >
                                        <td>
                                            <span className="tomPrimary">{demande.employeeMatricule || "N/A"}</span>
                                        </td>

                                        <td>
                                            <div className="tomName">
                                                <span className="tomPrimary">{demande.employeeName}</span>
                                                {demande.urgent && <span className="tomFire">ðŸ”¥</span>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tomType">
                                                <Briefcase size={16} />
                                                <div>
                                                    <div className="tomPrimary">{demande.ordreMissionLibelle}</div>
                                                    <div className="tomSub">{demande.ordreMissionCode}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tomPeriod">
                                                <div>{formatDate(demande.ordreMissionDateDebut)}</div>
                                                <div>- {formatDate(demande.ordreMissionDateFin)}</div>
                                            </div>
                                        </td>

                                        <td>
                                            {getMissionLocation(demande) ? (
                                                <div className="tomLocation">
                                                    <MapPin size={14} />
                                                    {getMissionLocation(demande)}
                                                </div>
                                            ) : "â€”"}
                                        </td>

                                        <td>
                                            <div className="tomJustif">
                                                <FileText size={14} />
                                                <span>{demande.justificatifs?.length || 0}</span>
                                            </div>
                                        </td>

                                        <td>
                                            <span className="tomMuted">{formatDate(demande.createdAt)}</span>
                                        </td>

                                        {/*<td>{getStatutBadge(demande.status)}</td>*/}

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            {demande.status === "PENDING_ADMIN" ? (
                                                <div className="tomRowActions">
                                                    <button
                                                        type="button"
                                                        className="tomIconBtn"
                                                        onClick={() => showDetails(demande)}
                                                        title="Voir détails"
                                                    >
                                                        <FileText size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="tomActionBtn tomActionBtn--ok"
                                                        onClick={() => handleApprove(demande.id)}
                                                        title="Approuver"
                                                        disabled={loading}
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="tomActionBtn tomActionBtn--no"
                                                        onClick={() => handleReject(demande)}
                                                        title="Refuser"
                                                        disabled={loading}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="tomIconBtn"
                                                    onClick={() => showDetails(demande)}
                                                    title="Voir détails"
                                                >
                                                    <FileText size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
                MODAL DÃ‰TAILS
            â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {showDetailsModal && selectedDemande && (
                <div className="tomModal">
                    <div className="tomModal__panel" role="dialog" aria-modal="true">
                        <div className="tomModal__header">
                            <div className="tomModal__headLeft">
                                <div className="tomModal__icon">
                                    <Briefcase size={18} />
                                </div>
                                <div>
                                    <div className="tomModal__title">Demande de mission #{selectedDemande.id}</div>
                                    <div className="tomModal__sub">Demandée le {formatDate(selectedDemande.createdAt)}</div>
                                </div>
                            </div>
                            <button type="button" className="tomIconBtn" onClick={closeDetails}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tomModal__body">
                            <div className="tomGrid2">
                                {/* Collaborateur */}
                                <div className="tomPanel">
                                    <div className="tomPanel__title">
                                        <User size={16} /> Collaborateur
                                    </div>
                                    <div className="tomKv"><span>Matricule</span><b>{selectedDemande.employeeMatricule || "â€”"}</b></div>
                                    <div className="tomKv"><span>Nom</span><b>{selectedDemande.employeeName}</b></div>
                                </div>

                                {/* Mission */}
                                <div className="tomPanel">
                                    <div className="tomPanel__title">
                                        <Briefcase size={16} /> Mission
                                    </div>
                                    <div className="tomKv"><span>Libellé</span><b>{selectedDemande.ordreMissionLibelle}</b></div>
                                    <div className="tomKv"><span>Code</span><b>{selectedDemande.ordreMissionCode}</b></div>
                                    <div className="tomKv"><span>Période</span>
                                        <b>{formatDate(selectedDemande.ordreMissionDateDebut)} - {formatDate(selectedDemande.ordreMissionDateFin)}</b>
                                    </div>
                                    {getMissionLocation(selectedDemande) && (
                                        <div className="tomKv"><span>Lieu</span><b>{getMissionLocation(selectedDemande)}</b></div>
                                    )}
                                    <div className="tomKv"><span>Workflow</span>
                                        <b>{selectedDemande.workflowLevel === "both" ? "Manager + RH" :
                                            selectedDemande.workflowLevel === "manager" ? "Manager" : "RH"}</b>
                                    </div>
                                </div>

                                {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                    <div className="tomPanel tomPanel--full">
                                        <div className="tomPanel__title">
                                            <FileText size={16} /> Justificatifs ({selectedDemande.justificatifs.length})
                                        </div>
                                        <div className="tomDocs">
                                            {selectedDemande.justificatifs.map((j, idx) => (
                                                <a key={idx} className="tomDoc" href={getDownloadUrl(j)} target="_blank" rel="noreferrer">
                                                    <FileText size={18} />
                                                    <span>{j}</span>
                                                    <Download size={16} />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Motif */}
                                {/*<div className="tomPanel tomPanel--full">*/}
                                {/*    <div className="tomPanel__title">*/}
                                {/*        <FileText size={16} /> Motif de la demande*/}
                                {/*    </div>*/}
                                {/*    <div className="tomText">{selectedDemande.motif || " - "}</div>*/}
                                {/*</div>*/}

                                {/* Décision Manager */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="tomPanel tomPanel--full">
                                        <div className="tomPanel__title">
                                            <Check size={16} /> Décision Manager
                                        </div>
                                        <div className="tomKv">
                                            <span>Décision</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && <div className="tomKv"><span>Par</span><b>{selectedDemande.managerName}</b></div>}
                                        {selectedDemande.managerDecisionAt && <div className="tomKv"><span>Date</span><b>{formatDate(selectedDemande.managerDecisionAt)}</b></div>}
                                        {selectedDemande.managerComment && (
                                            <div className="tomRejectBox">
                                                <div className="tomRejectLabel">Commentaire</div>
                                                <div className="tomRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Décision RH */}
                                {selectedDemande.status !== "PENDING_MANAGER" && selectedDemande.adminDecision !== null && (
                                    <div className={`tomPanel tomPanel--full ${selectedDemande.status === "APPROVED" ? "tomValidOk" : "tomValidNo"}`}>
                                        <div className="tomPanel__title">
                                            {selectedDemande.status === "APPROVED" ? <Check size={16} /> : <X size={16} />}
                                            {selectedDemande.status === "APPROVED" ? " Mission approuvée" : " Mission refusée"}
                                        </div>
                                        <div className="tomKv"><span>Date</span><b>{formatDate(selectedDemande.adminDecisionAt)}</b></div>
                                        {selectedDemande.adminName && <div className="tomKv"><span>Par</span><b>{selectedDemande.adminName}</b></div>}
                                        {selectedDemande.adminComment && (
                                            <div className="tomRejectBox">
                                                <div className="tomRejectLabel">
                                                    {selectedDemande.status === "APPROVED" ? "Commentaire" : "Motif du refus"}
                                                </div>
                                                <div className="tomRejectText">{selectedDemande.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="tomModal__footer">
                            {selectedDemande.status === "PENDING_ADMIN" ? (
                                <>
                                    <button
                                        className="tomBtn tomBtn--danger"
                                        onClick={() => { closeDetails(); handleReject(selectedDemande); }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        className="tomBtn tomBtn--ok"
                                        onClick={() => { handleApprove(selectedDemande.id); closeDetails(); }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                    <button className="tomBtn tomBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button className="tomBtn tomBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
                MODAL REJET
            â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {showRejectModal && selectedDemande && (
                <div className="tomModal">
                    <div className="tomModal__panel tomModal__panel--small">
                        <div className="tomModal__header">
                            <div className="tomModal__headLeft">
                                <div className="tomModal__icon tomModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tomModal__title">Refuser la demande de mission</div>
                                    <div className="tomModal__sub">
                                        {selectedDemande.employeeName} â€” {selectedDemande.ordreMissionLibelle}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tomIconBtn" onClick={closeReject}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tomModal__body">
                            <div className="tomMiniCard">
                                <div className="tomMiniRow">
                                    <span className="tomMuted">Mission</span>
                                    <b>{selectedDemande.ordreMissionCode} â€“ {selectedDemande.ordreMissionLibelle}</b>
                                </div>
                                <div className="tomMiniRow">
                                    <span className="tomMuted">Période</span>
                                    <b>{formatDate(selectedDemande.ordreMissionDateDebut)} - {formatDate(selectedDemande.ordreMissionDateFin)}</b>
                                </div>
                            </div>

                            <label className="tomLabel">
                                Motif du refus <span className="tomReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Expliquez pourquoi cette demande est refusée..."
                                className="tomTextarea"
                            />
                            <div className="tomHelp">Ce motif sera visible par le collaborateur</div>
                        </div>

                        <div className="tomModal__footer">
                            <button type="button" className="tomBtn tomBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tomBtn tomBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementMissions;
